import { QueryTable } from "@/components/query-table/QueryTable";
import PageHeader from "@/components/PageHeader";
import { PATH_PROJECT_RATE_EDIT } from "@/constants";
import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import { memo, useMemo } from "react";
import { useProjectBreadcrumb } from "@/modules/projects/hooks/useProjectBreadcrumb";
import { readRoleBasedProjectRates } from "@/schemas/roleBasedProjectRates";
import { ProjectDetailsAndNavigation } from "@/modules/projects/components/ProjectDetailsAndNavigation";
import { ReadRoleBasedProjectRates } from "@/schemas/roleBasedProjectRates";
import { ProjectRateAddButton } from "@/modules/projects/components/rates/ProjectRateAddButton";
import { ProjectRateListContent } from "@/modules/projects/components/rates/ProjectRateListContent";
import { Row } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { useProjectQuery } from "@/modules/projects/hooks/useProjectQuery";
import { useProjectId } from "@/modules/projects/hooks/useProjectId";
import { useProjectRateListQuery } from "@/modules/projects/components/rates/hooks/useProjectRateListQuery";
import { generatePath } from "react-router-dom";
import { MenubarContainer } from "@/components/MenubarContainer";

const ProjectRateList = () => {
  const name = "projectRates";
  const projectId = useProjectId();
  const { t } = useTranslation();
  const projectQuery = useProjectQuery(projectId);
  const title = t("projects.breadcrumb.rates");
  const breadcrumbItems = useProjectBreadcrumb(projectQuery, [
    {
      label: title,
    },
  ]);
  const { listQuery, options } = useProjectRateListQuery();
  const queryValue = useMemo(() => ({ query: listQuery }), [listQuery]);

  const columnOverrides = useMemo(
    () => ({
      project_role_name: {
        id: "project_role_name",
        cell: ({ row }: { row: Row<ReadRoleBasedProjectRates> }) =>
          row.original.project_role_name ?? t("projectRates.all.roles"),
      },
      store_name: {
        id: "store_name",
        cell: ({ row }: { row: Row<ReadRoleBasedProjectRates> }) =>
          row.original.store_name || t("projectRates.all.stores"),
      },
      settlement_type: {
        id: "settlement_type",
        cell: ({ row }: { row: Row<ReadRoleBasedProjectRates> }) =>
          t(`settlementType.${row.original.settlement_type}`),
      },
    }),
    [t],
  );

  return (
    <div className="space-y-2">
      <PageHeader breadcrumbItems={breadcrumbItems} title={title} />
      <ProjectDetailsAndNavigation />
      <QueryTable<ReadRoleBasedProjectRates>
        schema={readRoleBasedProjectRates}
        name={name}
        query={listQuery}
        options={options}
        pathBuilder={(row) =>
          generatePath(PATH_PROJECT_RATE_EDIT, {
            rateId: row.id,
            projectId,
          })
        }
        columnOverrides={columnOverrides}
        tableFeatures={{
          exportSettings: { moduleName: "role-based-project-rates" },
        }}
      >
        <DataTableQueryContextProvider value={queryValue}>
          <MenubarContainer>
            <QueryTable.Menubar className="mb-0 grow">
              <QueryTable.Menubar.Columns />
              <QueryTable.Menubar.Reset />
            </QueryTable.Menubar>
            <ProjectRateAddButton />
          </MenubarContainer>
          <ProjectRateListContent />
        </DataTableQueryContextProvider>
      </QueryTable>
    </div>
  );
};

export default memo(ProjectRateList);
